import type { FC, ReactElement } from 'react'

import { Button } from '@skytech/ui/Button'
import { getLocaleTimezoneIsoDateTime } from '@skytech/utils'
import { useMutation } from '@tanstack/react-query'
import { Formik, Form as FormikForm } from 'formik'

import Text from '@/app/translations/Text'
import type { InjuryReportForm } from '@/app/types'
import { useSubmitFormMessage, useInjuryReportForm } from '@/forms/hooks'

import AssetDescription from './AssetDescription'
import DamageDescription from './DamageDescription'
import InjuryReportStatus from './InjuryReportStatus'
import InjuryType from './InjuryType'
import PoliceAndInsuranceRef from './PoliceAndInsuranceRef'
import RepairInstructions from './RepairInstructions'
import injuryReportResources from './resources'
import type { InjuryReportPayload } from './types'

type FormFields = {
  insuranceRef: string
  policeReportRef: string
  injuryType: string
  damageLocation: string
  injuryDate: string
  damageCause: string
  damageDescription: string
}

const getInitValues = (data: InjuryReportForm) => ({
  insuranceRef: data.insuranceRef || '',
  policeReportRef: data.policeReportRef || '',
  injuryType: data.type || '',
  damageLocation: data.damageLocation || '',
  injuryDate: data.injuryDate ? getLocaleTimezoneIsoDateTime(data.injuryDate) : '',
  damageCause: data.damageCause || '',
  damageDescription: data.damageDescription || '',
})

const getSubmitValues = (data: FormFields) => ({
  insurance_ref: data.insuranceRef || null,
  police_report_ref: data.policeReportRef || null,
  type: data.injuryType,
  damage_location: data.damageLocation,
  injury_date: data.injuryDate,
  damage_cause: data.damageCause,
  damage_description: data.damageDescription || null,
})

export const Form: FC = () => {
  const { SubmitFormMessagePopup, onSubmitFormSuccess, onSubmitFormError } = useSubmitFormMessage()

  const { data, refetch } = useInjuryReportForm()
  if (!data) return null

  const offboardingMutation = useMutation(
    (formData: InjuryReportPayload) => injuryReportResources.post(formData),
    {
      onSuccess() {
        onSubmitFormSuccess()
        refetch()
      },
      onError: onSubmitFormError,
    }
  )

  return (
    <Formik
      validateOnBlur={false}
      initialValues={getInitValues(data)}
      validate={values => {
        const errors: Partial<Record<keyof FormFields, string | ReactElement>> = {}
        const {
          injuryDate,
          damageCause,
          insuranceRef,
          damageLocation,
          policeReportRef,
          damageDescription,
          injuryType,
        } = values

        if (!injuryType) {
          errors.injuryType = <Text id="Dette feltet er obligatorisk" />
        }

        if (!injuryDate) {
          errors.injuryDate = <Text id="Dette feltet er obligatorisk" />
        }

        if (!damageLocation) {
          errors.damageLocation = <Text id="Dette feltet er obligatorisk" />
        }

        if (!damageCause) {
          errors.damageCause = <Text id="Dette feltet er obligatorisk" />
        }

        if ((injuryType === 'LOSS' || injuryType === 'THEFT') && !insuranceRef) {
          errors.insuranceRef = <Text id="Dette feltet er obligatorisk" />
        }

        if (injuryType === 'THEFT' && !policeReportRef) {
          errors.policeReportRef = <Text id="Dette feltet er obligatorisk" />
        }

        if (injuryType === 'DAMAGE' && !damageDescription) {
          errors.damageDescription = <Text id="Dette feltet er obligatorisk" />
        }

        return errors
      }}
      onSubmit={formData => offboardingMutation.mutate(getSubmitValues(formData))}
    >
      {({ values }) => (
        <FormikForm>
          <InjuryType isEditable={data.isEditable} />

          <DamageDescription injuryType={values.injuryType} isEditable={data.isEditable} />

          <PoliceAndInsuranceRef injuryType={values.injuryType} isEditable={data.isEditable} />

          <section className="submit-section">
            <Button
              type="submit"
              disabled={!data?.isEditable}
              isLoading={offboardingMutation.isLoading}
            >
              <Text id="Send inn skjema" />
            </Button>
          </section>

          <SubmitFormMessagePopup />
        </FormikForm>
      )}
    </Formik>
  )
}

const InjuryReport = () => (
  <div className="injury-report">
    <InjuryReportStatus />

    <RepairInstructions />

    <AssetDescription />

    <Form />
  </div>
)

export default InjuryReport
